import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, Shield, ArrowRight, GitCompare, X, AlertCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { proofChainLinks, chainIntegrityTimeline, workflowProofChains } from '@/data/mockData';
import GradeTooltip from '@/components/GradeTooltip';

const gradeColor = { 'AUDIT-GRADE': '#10B981', 'OPERATIONS-GRADE': '#F59E0B', 'OBSERVATIONAL': '#94A3B8', 'UNAUDITED': '#64748B' };
const nodeTypeLabel = { CER: 'Classification Evidence Record', CSS: 'Control State at Decision Time', DER: 'Decision Evidence Record', OER: 'Oversight Evidence Record', PKG: 'Evidence Package' };

/* ─── Proof Node ─── */
function ProofNode({ node, expanded, onToggle, forensic }) {
  const isBroken = node.status === 'broken';
  const isWarn = node.status === 'warn';
  const isPending = node.status === 'pending';
  const borderColor = isBroken ? '#F59E0B' : isWarn ? '#F59E0B' : isPending ? '#475569' : '#10B981';
  const gc = gradeColor[node.grade] || '#94A3B8';

  return (
    <div data-testid={`proof-node-${node.id}`} className="relative flex-shrink-0 cursor-pointer transition-all" style={{ width: 220 }} onClick={onToggle}>
      <div className="rounded-xl border p-4 hover:border-opacity-60 transition-colors" style={{ borderColor: borderColor + '40', background: isBroken ? 'rgba(245,158,11,0.04)' : 'rgba(15,22,41,0.8)' }}>
        <div className="text-[8px] font-semibold text-[#475569] uppercase tracking-[0.15em] mb-2">{nodeTypeLabel[node.type]}</div>
        <div className="font-mono text-sm font-bold text-[#F8FAFC] mb-1.5">{node.id}</div>
        <GradeTooltip grade={node.grade}>
          <span className="text-[10px] font-mono px-2 py-0.5 rounded-full border cursor-help" style={{ color: gc, borderColor: gc + '30', background: gc + '10' }}>◆ {node.grade}</span>
        </GradeTooltip>
        {forensic && <div className="text-[9px] text-[#64748B] mt-1.5 mb-2">
          <span className="text-[#818CF8]">Reconstruction:</span> {node.method}
        </div>}
        {!forensic && <div className="mb-1" />}
        <div className="space-y-0.5 text-[10px]">
          <div className={isBroken ? 'text-[#F59E0B] font-semibold' : 'text-[#94A3B8]'}>{node.line1}</div>
          <div className={isBroken || isWarn ? 'text-[#F59E0B]' : 'text-[#94A3B8]'}>{node.line2}</div>
          <div className="text-[#64748B] font-mono">{node.line3}</div>
        </div>
        {isBroken && expanded && (
          <div className="mt-3 pt-3 border-t border-[#F59E0B]/20 space-y-1.5">
            <div className="text-[9px] text-[#F59E0B] font-semibold">CONTROL GAP AT DECISION TIME</div>
            <div className="text-[9px] text-[#94A3B8] whitespace-pre-line">{node.breakReason}</div>
            <div className="text-[9px]"><span className="text-[#475569]">Owner:</span> <span className="text-[#F8FAFC]">{node.owner}</span></div>
            <div className="text-[9px]"><span className="text-[#475569]">Action:</span> <span className="text-[#94A3B8]">{node.action}</span></div>
            <div className="text-[9px]"><span className="text-[#475569]">ETA:</span> <span className="text-[#94A3B8]">{node.eta}</span></div>
          </div>
        )}
        <div className="mt-2 text-[9px] text-[#6366F1] flex items-center gap-1">{expanded ? 'Collapse' : 'Expand'} <ArrowRight size={9} className={expanded ? 'rotate-90' : ''} /></div>
      </div>
      <div className="absolute -top-1.5 -right-1.5 w-3 h-3 rounded-full border-2 border-[#0A0A0F]" style={{ background: borderColor }} />
    </div>
  );
}

function ChainLink({ link, forensic }) {
  const broken = !link.verified;
  return (
    <div className="flex flex-col items-center justify-center flex-shrink-0" style={{ width: 120 }}>
      <div className={`w-full h-[2px] ${broken ? 'bg-[#F59E0B]/40' : 'bg-[#10B981]/30'}`} />
      <div className="text-center my-1.5">
        <div className={`font-mono text-[9px] ${broken ? 'text-[#F59E0B]' : 'text-[#64748B]'}`}>{link.hash.slice(0, 20)}...</div>
        <div className={`text-[8px] ${broken ? 'text-[#F59E0B]' : 'text-[#475569]'}`}>{link.ts}</div>
        {broken && link.breakMsg && <div className="text-[8px] text-[#F59E0B] font-semibold mt-0.5 whitespace-pre-line">{link.breakMsg}</div>}
        {!broken && <div className="text-[8px] text-[#10B981]">◆ Verified</div>}
      </div>
      <div className={`w-full h-[2px] ${broken ? 'bg-[#F59E0B]/40' : 'bg-[#10B981]/30'}`} />
    </div>
  );
}

/* ─── Temporal Slider States ─── */
const sliderDates = [
  { val: 0, label: 'Apr 12', state: 'clean' },
  { val: 1, label: 'Apr 13', state: 'clean' },
  { val: 2, label: 'Apr 14', state: 'clean' },
  { val: 3, label: 'Apr 15', state: 'broken' },
  { val: 4, label: 'Apr 16', state: 'restoring' },
  { val: 5, label: 'Apr 17', state: 'restored' },
];

const chainStates = {
  clean: [
    { type: 'CER', id: 'CER-2026-001', grade: 'AUDIT-GRADE', ok: true },
    { type: 'CSS', id: 'CSS-047832', grade: 'AUDIT-GRADE', ok: true, note: 'All controls active' },
    { type: 'DER', id: 'DER-047832', grade: 'AUDIT-GRADE', ok: true },
    { type: 'OER', id: 'OER-047832', grade: 'AUDIT-GRADE', ok: true },
    { type: 'PKG', id: 'PKG-Q1-003', grade: 'AUDIT-GRADE', ok: true },
  ],
  broken: [
    { type: 'CER', id: 'CER-2026-001', grade: 'AUDIT-GRADE', ok: true },
    { type: 'CSS', id: 'CSS-031204', grade: 'AUDIT-GRADE', ok: false, note: 'Control gap · 11:14 UTC' },
    { type: 'DER', id: 'DER-031204', grade: 'OPS-GRADE', ok: false },
    { type: 'OER', id: 'OER-031105', grade: 'OPS-GRADE', ok: false, note: 'Weak signals' },
    { type: 'PKG', id: 'PKG-Q1-004', grade: 'BLOCKED', ok: false },
  ],
  restoring: [
    { type: 'CER', id: 'CER-2026-001', grade: 'AUDIT-GRADE', ok: true },
    { type: 'CSS', id: 'CSS-047832', grade: 'AUDIT-GRADE', ok: true, note: 'Remediated · 14:30 UTC' },
    { type: 'DER', id: 'DER-047832', grade: 'AUDIT-GRADE', ok: true },
    { type: 'OER', id: 'OER-047832', grade: 'AUDIT-GRADE', ok: true, note: 'Re-verified' },
    { type: 'PKG', id: 'PKG-Q1-004', grade: 'PENDING', ok: false, note: 'Regenerating' },
  ],
  restored: [
    { type: 'CER', id: 'CER-2026-001', grade: 'AUDIT-GRADE', ok: true },
    { type: 'CSS', id: 'CSS-047832', grade: 'AUDIT-GRADE', ok: true },
    { type: 'DER', id: 'DER-047832', grade: 'AUDIT-GRADE', ok: true },
    { type: 'OER', id: 'OER-047832', grade: 'AUDIT-GRADE', ok: true },
    { type: 'PKG', id: 'PKG-Q1-003', grade: 'AUDIT-GRADE', ok: true, note: 'Regenerated' },
  ],
};

/* ─── Live CSS Mutation States — one per slider position ─── */
const cssStates = [
  { // 0: Apr 12
    ts: 'Apr 12, 2026 · 09:00 UTC', grade: 'AUDIT-GRADE', gradeOk: true,
    rows: [
      { label: 'Policy Version', value: 'v2.3-ldrs', ok: true, mark: '✅ Current' },
      { label: 'Data Source Allowlist', value: 'v4.1', ok: true, mark: '✅ All sources listed' },
      { label: 'Model Approval State', value: 'GPT-4o 2025-11', ok: true, mark: '✅ Approved' },
      { label: 'Evidence Coverage', value: '94%', ok: true, mark: '✅ Above threshold' },
      { label: 'Active Exceptions', value: 'None', ok: true, mark: '✅' },
    ],
    assessment: 'Fully compliant control environment',
  },
  { // 1: Apr 13
    ts: 'Apr 13, 2026 · 10:30 UTC', grade: 'AUDIT-GRADE', gradeOk: true,
    rows: [
      { label: 'Policy Version', value: 'v2.3-ldrs', ok: true, mark: '✅ Current' },
      { label: 'Data Source Allowlist', value: 'v4.1', ok: true, mark: '✅ All sources listed' },
      { label: 'Model Approval State', value: 'GPT-4o 2025-11', ok: true, mark: '✅ Approved' },
      { label: 'Evidence Coverage', value: '94%', ok: true, mark: '✅ Above threshold' },
      { label: 'Active Exceptions', value: 'None', ok: true, mark: '✅' },
    ],
    assessment: 'Fully compliant control environment',
  },
  { // 2: Apr 14
    ts: 'Apr 14, 2026 · 14:32 UTC', grade: 'AUDIT-GRADE', gradeOk: true,
    rows: [
      { label: 'Policy Version', value: 'v2.3-ldrs', ok: true, mark: '✅ Current' },
      { label: 'Data Source Allowlist', value: 'v4.1', ok: true, mark: '✅ All sources listed' },
      { label: 'Model Approval State', value: 'GPT-4o 2025-11', ok: true, mark: '✅ Approved' },
      { label: 'Evidence Coverage', value: '94%', ok: true, mark: '✅ Above threshold' },
      { label: 'Active Exceptions', value: 'None', ok: true, mark: '✅' },
    ],
    assessment: 'Fully compliant control environment',
  },
  { // 3: Apr 15 — broken (the moment)
    ts: 'Apr 15, 2026 · 11:14 UTC', grade: 'OPERATIONS-GRADE', gradeOk: false,
    rows: [
      { label: 'Policy Version', value: 'v2.1-ldrs', ok: false, mark: '⚠ Modified 2 hours prior' },
      { label: 'Data Source Allowlist', value: 'v4.1', ok: false, mark: '⚠ External Legal Corpus NOT listed' },
      { label: 'Model Approval State', value: 'GPT-4o 2025-11', ok: true, mark: '✅ Approved' },
      { label: 'Evidence Coverage', value: '71%', ok: false, mark: '⚠ Below 80% threshold' },
      { label: 'Active Exceptions', value: 'None', ok: false, mark: '⚠ No exception approved' },
    ],
    assessment: 'CONTROL GAP — 143 decisions affected',
  },
  { // 4: Apr 16 — restoring
    ts: 'Apr 16, 2026 · 14:30 UTC', grade: 'AUDIT-GRADE', gradeOk: true,
    rows: [
      { label: 'Policy Version', value: 'v2.3-ldrs', ok: true, mark: '✅ Restored' },
      { label: 'Data Source Allowlist', value: 'v4.1', ok: true, mark: '✅ External Legal Corpus re-verified' },
      { label: 'Model Approval State', value: 'GPT-4o 2025-11', ok: true, mark: '✅ Approved' },
      { label: 'Evidence Coverage', value: '88%', ok: true, mark: '✅ Restoring to threshold' },
      { label: 'Active Exceptions', value: 'None', ok: true, mark: '✅' },
    ],
    assessment: 'Control state remediation in progress',
  },
  { // 5: Apr 17 — restored
    ts: 'Apr 17, 2026 · 09:00 UTC', grade: 'AUDIT-GRADE', gradeOk: true,
    rows: [
      { label: 'Policy Version', value: 'v2.3-ldrs', ok: true, mark: '✅ Restored' },
      { label: 'Data Source Allowlist', value: 'v4.1', ok: true, mark: '✅ External Legal Corpus re-verified' },
      { label: 'Model Approval State', value: 'GPT-4o 2025-11', ok: true, mark: '✅ Approved' },
      { label: 'Evidence Coverage', value: '94%', ok: true, mark: '✅ Restored' },
      { label: 'Active Exceptions', value: 'None', ok: true, mark: '✅' },
    ],
    assessment: 'Control state fully restored',
  },
];

function CompareView({ onClose }) {
  const [sliderVal, setSliderVal] = useState(2); // Start at Apr 14
  const [reconstructing, setReconstructing] = useState(false);
  const [reconstructed, setReconstructed] = useState(false);
  const [reconStep, setReconStep] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [playSpeed, setPlaySpeed] = useState(1500);
  const [eventLog, setEventLog] = useState([]);
  const [flashNode, setFlashNode] = useState(null);
  const playRef = useRef(null);

  const sd = sliderDates[sliderVal];
  const nodes = chainStates[sd.state];
  const isBroken = sd.state === 'broken';
  const prevSlider = useRef(sliderVal);

  const cssStatus = {
    clean: { text: 'Fully compliant · All controls active · Policy v2.3', color: '#10B981', icon: '◆' },
    broken: { text: 'CONTROL GAP · External Legal Corpus · No exception active', color: '#F59E0B', icon: '⚠' },
    restoring: { text: 'Remediated · CSS coverage restored to 94%', color: '#10B981', icon: '◆' },
    restored: { text: 'Fully compliant · Chain restored · All controls active', color: '#10B981', icon: '◆' },
  }[sd.state];

  const eventLogEntries = [
    { date: 'Apr 12', icon: '◆', text: 'Governance state: nominal · All controls active', color: '#10B981' },
    { date: 'Apr 13', icon: '◆', text: 'No changes detected · Chain intact', color: '#10B981' },
    { date: 'Apr 14', icon: '◆', text: 'CSS enriched with ServiceNow GRC corroboration', color: '#10B981' },
    { date: 'Apr 15', icon: '⚠', text: 'CONTROL GAP INTRODUCED · External Legal Corpus · Chain degraded', color: '#F59E0B' },
    { date: 'Apr 16', icon: '◆', text: 'Remediation initiated · CSS controls restored', color: '#10B981' },
    { date: 'Apr 17', icon: '◆', text: 'Evidence package regenerated · Chain fully restored', color: '#10B981' },
  ];

  // Event log responds to ANY slider movement (auto-play or manual drag)
  // + rewind indicator when slider moves backward
  useEffect(() => {
    if (sliderVal !== prevSlider.current) {
      const prev = prevSlider.current;
      const movedBack = sliderVal < prev;
      const entry = eventLogEntries[sliderVal];
      if (entry) {
        setEventLog(curr => {
          const last = curr[curr.length - 1];
          if (last && last.date === entry.date && !movedBack) return curr;
          const additions = movedBack
            ? [{ date: '', icon: '↩', text: `rewound to ${entry.date}`, color: '#475569', rewind: true }, entry]
            : [entry];
          return [...curr, ...additions].slice(-6);
        });
      }
    }
    // Flash CSS node when crossing into broken / restoring states
    if (sliderVal === 3 && prevSlider.current !== 3) {
      setFlashNode('amber');
      setTimeout(() => setFlashNode(null), 450);
    } else if (sliderVal === 4 && prevSlider.current !== 4) {
      setFlashNode('green');
      setTimeout(() => setFlashNode(null), 450);
    }
    prevSlider.current = sliderVal;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sliderVal]);

  const startPlay = () => {
    setPlaying(true); setSliderVal(0); setReconstructed(false); setEventLog([eventLogEntries[0]]);
    let pos = 0;
    playRef.current = setInterval(() => { pos++; if (pos > 5) { clearInterval(playRef.current); setPlaying(false); return; } setSliderVal(pos); }, playSpeed);
  };
  const stopPlay = () => { clearInterval(playRef.current); setPlaying(false); };
  useEffect(() => () => { if (playRef.current) clearInterval(playRef.current); }, []);

  const runReconstruct = () => {
    setReconstructing(true);
    setReconStep(0);
    const steps = [1, 2, 3, 4];
    steps.forEach((s, i) => setTimeout(() => setReconStep(s), (i + 1) * 600));
    setTimeout(() => { setReconstructing(false); setReconstructed(true); }, 2800);
  };

  return (
    <div className="glass-card p-6 space-y-5" data-testid="proof-chain-compare">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold text-[#F8FAFC] uppercase tracking-wider flex items-center gap-2">
          <Clock size={14} className="text-[#6366F1]" />
          {playing && <span className="w-2 h-2 rounded-full bg-[#EF4444] animate-pulse" />}
          {playing ? 'LIVE REPLAY · Temporal Reconstruction Engine' : 'Temporal Reconstruction Engine · Legal Document Drafting Pipeline'}
        </h3>
        <div className="flex items-center gap-2">
          <span className="text-[9px] font-mono text-[#6366F1]/60">Chain Integrity Engine · Real-time</span>
          <button onClick={onClose} className="text-[#475569] hover:text-[#F8FAFC]"><X size={16} /></button>
        </div>
      </div>

      {/* CSS Status Indicator */}
      <div className="text-center text-[10px] font-mono transition-colors duration-500" style={{ color: cssStatus.color }}>
        Control State at Decision Time: {cssStatus.icon} {cssStatus.text}
      </div>

      {/* Slider with auto-play */}
      <div className="px-2">
        <div className="flex items-center gap-3">
          <button onClick={playing ? stopPlay : startPlay} className="text-xs text-[#6366F1] hover:text-[#818cf8] border border-[#6366F1]/30 px-2.5 py-1 rounded-lg flex-shrink-0 transition-colors" data-testid="replay-play-btn">
            {playing ? '⏸ Pause' : '▶ Play'}
          </button>
          <button onClick={() => setPlaySpeed(playSpeed === 1500 ? 750 : 1500)} className="text-[9px] text-[#475569] hover:text-[#94A3B8] border border-[#1E2A45] px-2 py-1 rounded-lg flex-shrink-0" data-testid="replay-speed">
            {playSpeed === 1500 ? '1x' : '2x'}
          </button>
          <input
            type="range" min="0" max="5" value={sliderVal}
            onChange={(e) => { setSliderVal(Number(e.target.value)); setReconstructed(false); if (playing) stopPlay(); }}
            className="w-full accent-[#6366F1] cursor-pointer"
            data-testid="temporal-slider"
            style={{ height: 6 }}
          />
        </div>
        <div className="flex justify-between mt-1">
          {sliderDates.map(d => (
            <span key={d.val} className={`text-[9px] font-mono ${d.val === sliderVal ? 'text-[#F8FAFC] font-semibold' : 'text-[#475569]'}`}>{d.label}</span>
          ))}
        </div>
        <div className="text-center mt-1 text-[10px] text-[#6366F1]">
          {sd.label}, 2026 · {sd.state === 'broken' ? '11:14 UTC · Control gap active' : sd.state === 'restoring' ? '14:30 UTC · Remediating' : sd.state === 'restored' ? '09:00 UTC · Chain restored' : 'Governance state: compliant'}
        </div>
      </div>

      {/* ─── Live CSS Mutation Panel — updates as slider moves ─── */}
      {(() => {
        const css = cssStates[sliderVal];
        const gradeColor = css.gradeOk ? '#10B981' : '#F59E0B';
        return (
          <div className="glass-card p-4 transition-colors duration-500" data-testid="live-css-panel" style={{ borderLeftWidth: 2, borderLeftColor: gradeColor + '60' }}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[8px] font-semibold text-[#475569] uppercase tracking-[0.15em]">CONTROL STATE AT DECISION TIME — LIVE VIEW</div>
              <div className="text-[9px] font-mono transition-colors duration-500" style={{ color: gradeColor }}>
                {css.ts} · {css.gradeOk ? '◆' : '⚠'} {css.grade}
              </div>
            </div>
            <div className="space-y-0.5">
              {css.rows.map(r => (
                <div key={r.label} className="flex items-start gap-2 text-[10px] font-mono transition-colors duration-500">
                  <span className="text-[#64748B] w-44 flex-shrink-0">{r.label}:</span>
                  <span className="text-[#F8FAFC] w-32 flex-shrink-0">{r.value}</span>
                  <span style={{ color: r.ok ? '#10B981' : '#F59E0B' }} className="transition-colors duration-500">{r.mark}</span>
                </div>
              ))}
              <div className="flex items-start gap-2 text-[10px] font-mono pt-1.5 mt-1 border-t border-[#1E2A45] transition-all duration-300">
                <span className="text-[#64748B] w-44 flex-shrink-0">Governance assessment:</span>
                <span style={{ color: gradeColor }} className="transition-colors duration-500">{css.gradeOk ? '◆' : '⚠'} {css.assessment}</span>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Chain nodes — animate between states */}
      <div className="flex items-center justify-center gap-2 min-w-0 overflow-x-auto pb-2">
        {nodes.map((n, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className={`p-3.5 rounded-xl border transition-all duration-500 ${n.ok ? 'border-[#10B981]/30 bg-[#10B981]/[0.03]' : 'border-[#F59E0B]/30 bg-[#F59E0B]/[0.03]'} ${n.type === 'CSS' && flashNode === 'amber' ? 'node-flash-amber' : ''} ${n.type === 'CSS' && flashNode === 'green' ? 'node-flash-green' : ''}`} style={{ width: 140 }}>              <div className="text-[8px] font-semibold text-[#475569] uppercase tracking-wider">{nodeTypeLabel[n.type]}</div>
              <div className="font-mono text-[11px] font-bold text-[#F8FAFC] mt-1">{n.id}</div>
              <div className={`text-[9px] mt-1 transition-colors duration-500 ${n.ok ? 'text-[#10B981]' : n.grade === 'BLOCKED' || n.grade === 'PENDING' ? 'text-[#475569]' : 'text-[#F59E0B]'}`}>
                {n.grade === 'BLOCKED' ? '⏸ BLOCKED' : n.grade === 'PENDING' ? '⏳ PENDING' : `◆ ${n.grade}`}
              </div>
              {n.note && <div className={`text-[8px] mt-0.5 ${n.ok ? 'text-[#64748B]' : 'text-[#F59E0B]'}`}>{n.note}</div>}
            </div>
            {i < nodes.length - 1 && <div className={`w-8 h-[2px] transition-colors duration-500 ${n.ok ? 'bg-[#10B981]/30' : 'bg-[#F59E0B]/30'}`} />}
          </div>
        ))}
      </div>

      {/* Risk propagation counter */}
      {isBroken && (
        <div className="text-center transition-opacity duration-500">
          <div className="text-[#F59E0B] text-sm font-mono font-semibold">⚠ 143 decisions executed during this control gap</div>
          <div className="text-[10px] text-[#F59E0B]/70 mt-0.5">Risk propagated to evidence packages</div>
        </div>
      )}

      {/* Event log during playback */}
      {eventLog.length > 0 && (
        <div className="space-y-1 transition-opacity duration-300" data-testid="replay-event-log">
          {eventLog.map((evt, i) => (
            <div key={i} className="flex items-center gap-2 text-[9px] font-mono transition-opacity duration-300" style={{ opacity: evt.rewind ? 0.45 : (i === eventLog.length - 1 ? 1 : 0.5) }}>
              <span className="text-[#475569] w-10">{evt.date}</span>
              <span style={{ color: evt.color }}>{evt.icon}</span>
              <span style={{ color: evt.color, fontStyle: evt.rewind ? 'italic' : 'normal' }}>{evt.text}</span>
            </div>
          ))}
        </div>
      )}

      {/* Status line */}
      <div className={`text-center text-[10px] font-semibold ${
        sd.state === 'clean' ? 'text-[#10B981]' : sd.state === 'restored' ? 'text-[#10B981]' : sd.state === 'restoring' ? 'text-[#6366F1]' : 'text-[#F59E0B]'
      }`}>
        {sd.state === 'clean' ? '◆ COMPLETE GOVERNANCE PROOF CHAIN' : sd.state === 'broken' ? '◆ PROOF CHAIN BROKEN — CONTROL GAP ACTIVE' : sd.state === 'restoring' ? '◆ CHAIN RESTORING — CSS REMEDIATED' : '◆ CHAIN RESTORED · All nodes AUDIT-GRADE'}
      </div>

      {/* Reconstruct button — only on broken state */}
      {isBroken && !reconstructed && !reconstructing && (
        <div className="text-center">
          <button onClick={runReconstruct} className="bg-[#6366F1] hover:bg-[#818cf8] text-white text-xs font-medium px-5 py-2.5 rounded-lg transition-all shadow-[0_0_10px_rgba(99,102,241,0.15)]" data-testid="reconstruct-btn">
            ◆ Reconstruct governance state at this moment →
          </button>
        </div>
      )}

      {/* Reconstruction animation */}
      {reconstructing && (
        <div className="space-y-2 p-4 rounded-xl bg-[#080D1A] border border-[#6366F1]/20" data-testid="reconstruct-animation">
          {[
            { step: 1, text: 'Temporal Reconstruction Engine: querying evidence fabric...' },
            { step: 2, text: 'Correlating 143 affected DERs across proof graph...' },
            { step: 3, text: 'Chain Integrity Engine: evaluating package validity...' },
            { step: 4, text: 'Governance state at 11:14 UTC — fully reconstructed.' },
          ].map(s => (
            <div key={s.step} className={`flex items-center gap-2 text-[10px] font-mono transition-opacity duration-300 ${reconStep >= s.step ? 'opacity-100' : 'opacity-20'}`}>
              {reconStep > s.step ? <CheckCircle2 size={12} className="text-[#10B981]" /> : reconStep === s.step ? <div className="w-3 h-3 rounded-full border-2 border-[#6366F1] border-t-transparent animate-spin" /> : <div className="w-3 h-3 rounded-full bg-[#1E2A45]" />}
              <span className="text-[#94A3B8]">{s.text}</span>
            </div>
          ))}
        </div>
      )}

      {/* Reconstructed state */}
      {reconstructed && (
        <div className="p-5 rounded-xl bg-[#080D1A] border border-[#6366F1]/20" data-testid="reconstructed-state">
          <div className="text-[10px] font-bold text-[#F8FAFC] uppercase tracking-wider mb-3">Reconstructed Governance State</div>
          <div className="text-[9px] text-[#64748B] font-mono mb-3">April 15, 2026 · 11:14 UTC · Legal Document Drafting</div>
          <div className="space-y-1.5">
            {[
              { label: 'Classification', value: 'HIGH-RISK · Deployer · CER v3.2', ok: true },
              { label: 'Control State', value: 'CONTROL GAP · External Legal Corpus not on allowlist', ok: false },
              { label: 'Evidence Coverage', value: '71% — Below threshold at this moment', ok: false },
              { label: 'Human Oversight', value: 'Mandatory · Met', ok: true },
              { label: 'Package Status', value: 'Would be invalid at this timestamp', ok: false },
              { label: 'Affected Decisions', value: '143 DERs share this upstream source', ok: false },
            ].map(r => (
              <div key={r.label} className="flex items-start gap-2 text-[11px]">
                <span className="text-[#64748B] w-36 flex-shrink-0">{r.label}:</span>
                <span className={r.ok ? 'text-[#10B981]' : 'text-[#F59E0B]'}>{r.ok ? '✅' : '⚠'} {r.value}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-[#1E2A45] text-[10px] text-[#94A3B8]">
            This reconstruction proves what was true at 11:14 UTC on April 15. Signed and immutable.
          </div>
        </div>
      )}

      {/* Side-by-side snapshot (always visible below) */}
      <div className="border-t border-[#1E2A45] pt-4">
        <div className="text-[9px] text-[#475569] uppercase tracking-wider mb-3 text-center">Governance State Snapshot</div>
        <div className="grid grid-cols-[1fr_auto_1fr] gap-0">
          <div className="rounded-xl border border-[#10B981]/20 p-4 bg-[#10B981]/[0.02]">
            <div className="text-xs font-semibold text-[#10B981] mb-0.5">April 14 — BEFORE</div>
            <div className="text-[9px] text-[#64748B] mb-2">All controls active, chain complete</div>
            {['CER · AUDIT-GRADE', 'CSS · All controls active', 'DER · AUDIT-GRADE', 'OER · Defensible signals', 'PKG · Complete'].map((l, i) => (
              <div key={i} className="flex items-center gap-1.5 py-1 text-[10px] text-[#94A3B8]"><CheckCircle2 size={10} className="text-[#10B981]" />{l}</div>
            ))}
          </div>
          <div className="flex flex-col items-center justify-center px-3">
            <div className="w-px h-full bg-[#1E2A45]" />
            <div className="text-[9px] text-[#6366F1] font-semibold py-2 whitespace-nowrap">← What changed →</div>
            <div className="w-px h-full bg-[#1E2A45]" />
          </div>
          <div className="rounded-xl border border-[#F59E0B]/20 p-4 bg-[#F59E0B]/[0.02]">
            <div className="text-xs font-semibold text-[#F59E0B] mb-0.5">April 15 — AFTER</div>
            <div className="text-[9px] text-[#64748B] mb-2">Data source outside allowlist, chain broken</div>
            {[{ t: 'CER · AUDIT-GRADE', ok: true }, { t: 'CSS · CONTROL GAP', ok: false }, { t: 'DER · Integrity warning', ok: false }, { t: 'OER · Weak signals', ok: false }, { t: 'PKG · Blocked', ok: false }].map((r, i) => (
              <div key={i} className="flex items-center gap-1.5 py-1 text-[10px]" style={{ color: r.ok ? '#94A3B8' : '#F59E0B' }}>{r.ok ? <CheckCircle2 size={10} className="text-[#10B981]" /> : <AlertTriangle size={10} className="text-[#F59E0B]" />}{r.t}</div>
            ))}
          </div>
        </div>
        <div className="text-[10px] text-[#F59E0B] text-center mt-3 px-3 py-1.5 rounded-lg bg-[#F59E0B]/5 border border-[#F59E0B]/20">
          CSS policy v2.1 modified · External Legal Corpus introduced without allowlist approval · Control gap active at 11:14 UTC
        </div>
      </div>
    </div>
  );
}

/* ─── Chain Integrity Timeline ─── */
function ChainTimeline() {
  return (
    <div className="glass-card p-6" data-testid="chain-integrity-timeline">
      <h3 className="text-xs font-semibold text-[#F8FAFC] uppercase tracking-wider mb-1">Chain Integrity Timeline · Proof Graph Service</h3>
      <div className="text-[9px] text-[#64748B] mb-5">Temporal record of proof quality changes · Legal Document Drafting Pipeline · Last 14 days</div>
      <div className="relative pl-6">
        {/* Connector line */}
        <div className="absolute left-[7px] top-2 bottom-2 w-[2px] bg-[#1E2A45]" />
        <div className="space-y-5">
          {chainIntegrityTimeline.map((evt, i) => (
            <div key={i} className="relative">
              <div className={`absolute left-[-21px] top-1 w-3 h-3 rounded-full border-2 border-[#0A0A0F] ${evt.status === 'warn' ? 'bg-[#F59E0B]' : 'bg-[#10B981]'}`} />
              <div className="font-mono text-[10px] text-[#64748B] mb-0.5">{evt.date}</div>
              <div className={`text-xs font-semibold ${evt.status === 'warn' ? 'text-[#F59E0B]' : 'text-[#F8FAFC]'}`}>
                {evt.status === 'warn' ? '⚠' : '◆'} {evt.title}
              </div>
              <div className="text-[10px] text-[#94A3B8] mt-0.5">{evt.detail}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Unaudited Chain State ─── */
function UnauditedChain() {
  return (
    <>
      <div className="glass-card p-6 overflow-x-auto">
        <div className="text-[9px] font-semibold text-[#475569] uppercase tracking-[0.15em] mb-5">Claims Document Processing · Unaudited System</div>
        <div className="flex items-center gap-0 min-w-max pb-2">
          {['CER', 'CSS', 'DER', 'OER', 'PKG'].map((type, i) => (
            <div key={type} className="flex items-center">
              <div className="relative flex-shrink-0" style={{ width: 220 }}>
                <div className="rounded-xl border border-[#475569]/30 p-4 bg-[#080D1A]">
                  <div className="text-[8px] font-semibold text-[#475569] uppercase tracking-[0.15em] mb-2">{nodeTypeLabel[type]}</div>
                  <div className="font-mono text-sm font-bold text-[#475569] mb-1.5">—</div>
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded-full border border-[#475569]/30 bg-[#475569]/10 text-[#64748B]">○ UNAUDITED</span>
                  <div className="text-[10px] text-[#475569] mt-2">No record exists</div>
                </div>
                <div className="absolute -top-1.5 -right-1.5 w-3 h-3 rounded-full border-2 border-[#0A0A0F] bg-[#475569]" />
              </div>
              {i < 4 && (
                <div className="flex flex-col items-center justify-center flex-shrink-0" style={{ width: 120 }}>
                  <div className="w-full h-[2px] bg-[#475569]/20" />
                  <div className="text-[8px] text-[#475569] my-1">No link</div>
                  <div className="w-full h-[2px] bg-[#475569]/20" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-xl border border-[#EF4444]/20 bg-[#EF4444]/[0.03] p-4 flex items-start gap-3" data-testid="unaudited-chain-status">
        <AlertCircle size={18} className="text-[#EF4444] flex-shrink-0 mt-0.5" />
        <div>
          <div className="text-sm font-semibold text-[#EF4444]">INCOMPLETE GOVERNANCE PROOF CHAIN — UNAUDITED SYSTEM</div>
          <div className="text-[10px] text-[#94A3B8] mt-1">This AI system has not been audited. No Classification Evidence Record exists. No Control State has been captured. 0/8 compliance controls are mapped. This system is generating decisions without a governance proof chain.</div>
          <div className="text-[10px] text-[#EF4444] mt-1">Risk: Any decisions made by this system cannot be included in audit packages or evidence exports.</div>
          <div className="flex items-center gap-3 mt-2">
            <button onClick={() => toast.info('Starting classification review for Claims Document Processing')} className="text-[10px] font-medium text-[#EF4444] border border-[#EF4444]/30 hover:bg-[#EF4444]/10 px-3 py-1.5 rounded-lg transition-colors">Start Classification Review →</button>
            <Link to="/dashboard/ai-inventory" className="text-[10px] text-[#6366F1] hover:text-[#818cf8]">View in AI Inventory →</Link>
          </div>
        </div>
      </div>
    </>
  );
}

/* ═══ MAIN COMPONENT ═══ */
export default function ProofChainView() {
  const [workflow, setWorkflow] = useState('contract-class');
  const [expandedNode, setExpandedNode] = useState(null);
  const [showCompare, setShowCompare] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);
  const [forensicDetail, setForensicDetail] = useState(false);

  const wf = workflowProofChains.find(w => w.id === workflow) || workflowProofChains[1];
  const isUnaudited = wf.scenario === 'unaudited';
  const chain = isUnaudited ? null : wf.scenario === 'clean' ? proofChainLinks.clean : proofChainLinks.broken;
  const allOk = chain ? chain.nodes.every(n => n.status === 'ok') : false;

  return (
    <div data-testid="proof-chain-view" className="space-y-5">
      {/* Build E — Narrative strip */}
      <div>
        <div className="text-[11px] text-[#64748B]">
          <span className="text-[#94A3B8] font-medium">GOVERNANCE PROOF OVER TIME</span> <span className="text-[#475569]">· Chain Integrity Engine</span>
        </div>
        <div className="text-[10px] text-[#475569] mt-0.5">Signed reconstruction of AI governance truth at any point in time</div>
        <div className="flex items-center gap-6 mt-2 text-[9px]">
          <span className="text-[#10B981]">◆ AUDIT-GRADE chain — All nodes signed, corroborated, complete</span>
          <span className="text-[#F59E0B]">◆ OPERATIONS-GRADE — One or more nodes partially reconstructed or with gaps</span>
          <span className="text-[#64748B]">○ UNAUDITED — No Classification Evidence Record — decisions ungoverned</span>
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div />
        <div className="flex items-center gap-2">
          <button onClick={() => setForensicDetail(!forensicDetail)} className={`text-[10px] px-3 py-1.5 rounded-lg border transition-colors ${forensicDetail ? 'border-[#6366F1]/40 text-[#6366F1] bg-[#6366F1]/10' : 'border-[#1E2A45] text-[#475569] hover:text-[#94A3B8]'}`} data-testid="forensic-toggle">
            {forensicDetail ? '◆ Forensic detail' : '☰ Forensic detail'}
          </button>
          <select data-testid="proof-chain-workflow" value={workflow} onChange={(e) => { setWorkflow(e.target.value); setExpandedNode(null); setShowCompare(false); }}
            className="text-xs bg-[#0F1629] border border-[#1E2A45] text-[#F8FAFC] rounded-lg px-3 py-2 focus:outline-none focus:border-[#6366F1]">
            {workflowProofChains.map(w => (
              <option key={w.id} value={w.id}>
                {w.name} {w.grade === 'UNAUDITED' ? '- UNAUDITED' : `- ${w.grade}`}
              </option>
            ))}
          </select>
          {!isUnaudited && (
            <button data-testid="proof-chain-compare-btn" onClick={() => setShowCompare(!showCompare)}
              className="flex items-center gap-1.5 text-xs text-[#6366F1] hover:text-[#818cf8] border border-[#6366F1]/30 px-3 py-2 rounded-lg transition-colors">
              <GitCompare size={13} /> Compare
            </button>
          )}
        </div>
      </div>

      {showCompare && <CompareView onClose={() => setShowCompare(false)} />}

      {isUnaudited ? (
        <UnauditedChain />
      ) : (
        <>
          {/* Chain visualization */}
          <div className="glass-card p-6 overflow-x-auto">
            <div className="text-[9px] font-semibold text-[#475569] uppercase tracking-[0.15em] mb-5">{chain.scenario}</div>
            <div className="flex items-center gap-0 min-w-max pb-2">
              {chain.nodes.map((node, i) => (
                <div key={node.id} className="flex items-center">
                  <ProofNode node={node} expanded={expandedNode === node.id} onToggle={() => setExpandedNode(expandedNode === node.id ? null : node.id)} forensic={forensicDetail} />
                  {i < chain.links.length && <ChainLink link={chain.links[i]} forensic={forensicDetail} />}
                </div>
              ))}
            </div>
          </div>

          {/* Chain status footer */}
          <div className={`rounded-xl border p-4 flex items-center gap-3 ${allOk ? 'border-[#10B981]/20 bg-[#10B981]/[0.03]' : 'border-[#F59E0B]/20 bg-[#F59E0B]/[0.03]'}`} data-testid="proof-chain-status">
            <Shield size={18} className={allOk ? 'text-[#10B981]' : 'text-[#F59E0B]'} />
            {allOk ? (
              <div>
                <div className="text-sm font-semibold text-[#10B981]">◆ COMPLETE GOVERNANCE PROOF CHAIN</div>
                <div className="text-[10px] text-[#64748B]">Classification · Control State · Decision · Oversight · Package — All ◆ AUDIT-GRADE · 5 independent corroborating sources</div>
              </div>
            ) : (
              <div>
                <div className="text-sm font-semibold text-[#F59E0B]">◆ PROOF CHAIN INCOMPLETE — REMEDIATION REQUIRED</div>
                <div className="text-[10px] text-[#94A3B8] mt-0.5">Chain break detected at Control State layer. {chain.nodes.find(n => n.status === 'broken')?.breakReason?.split('\n')[0]}</div>
                <div className="flex items-center gap-3 mt-2">
                  <button onClick={() => toast.info('Opening remediation workflow')} className="text-[10px] font-medium text-[#F59E0B] border border-[#F59E0B]/30 hover:bg-[#F59E0B]/10 px-3 py-1.5 rounded-lg transition-colors">Start Remediation</button>
                  <Link to="/dashboard/incident-review" className="text-[10px] text-[#6366F1] hover:text-[#818cf8]">View Investigation →</Link>
                </div>
              </div>
            )}
          </div>

          {/* Compare CTA — only for broken chain */}
          {!allOk && (
            <div className="rounded-xl bg-[#6366F1]/10 border border-[#6366F1]/30 p-4 flex items-center justify-between" data-testid="compare-cta">
              <div>
                <div className="text-sm font-semibold text-[#F8FAFC]">◆ SEE THE GOVERNANCE STATE SHIFT</div>
                <div className="text-[10px] text-[#94A3B8] mt-0.5">This workflow's proof chain changed on April 15, 2026. Compare the governance state before and after the control gap was introduced.</div>
              </div>
              <button onClick={() => setShowCompare(true)} className="flex items-center gap-1.5 bg-[#6366F1] hover:bg-[#818cf8] text-white text-xs font-medium px-4 py-2.5 rounded-lg transition-all shadow-[0_0_8px_rgba(99,102,241,0.15)] flex-shrink-0 ml-4">
                <GitCompare size={14} /> Compare April 14 vs April 15 →
              </button>
            </div>
          )}

          {/* Chain Integrity Timeline — collapsible */}
          <div>
            <button onClick={() => setShowTimeline(!showTimeline)} className="flex items-center gap-2 text-[10px] text-[#6366F1] hover:text-[#818cf8]" data-testid="toggle-timeline">
              {showTimeline ? '▼ Hide timeline' : '▶ Show timeline →'}
            </button>
            {showTimeline && <div className="mt-3"><ChainTimeline /></div>}
          </div>

          {/* Proof Graph Summary */}
          <div className="text-[9px] font-mono text-[#475569] space-y-0.5" data-testid="proof-graph-summary">
            <div className="text-[9px] font-semibold uppercase tracking-[0.12em] text-[#475569] mb-1">PROOF GRAPH SUMMARY · Proof Graph Service</div>
            {allOk ? (
              <>
                <div>Nodes: 5 governance records linked</div>
                <div>Links: 4 signed hash connections</div>
                <div>State: <span className="text-[#10B981]">◆ Chain intact · All dependencies verified</span></div>
                <div>Depth: CER → CSS → DER → OER → Package</div>
                <div>Lineage: Meridian Global · {chain.scenario} · Apr 15, 2026</div>
              </>
            ) : (
              <>
                <div>Nodes: 5 governance records · 1 degraded</div>
                <div>Links: 4 connections · 1 integrity failure</div>
                <div>State: <span className="text-[#F59E0B]">⚠ Chain degraded at CSS layer · Restorability: Yes</span></div>
                <div>Depth: CER ✅ → CSS ⚠ → DER ⚠ → OER ⚠ → Package ⏸</div>
                <div>Lineage: Meridian Global · Legal Document Drafting · Apr 15, 2026 · 11:14 UTC</div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
